import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Renderer2,
  Self
} from '@angular/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { FormControlName, NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[appInputNumber]'
})
export class InputNumberDirective extends InputDirective implements AfterViewInit, OnDestroy {
  private valueSub?: Subscription;
  private normalizing = false;

  @Input() public decimals?: number;
  @Input() public selectOnFocus: boolean = false;

  @HostListener('focus')
  public onFocus(): void {
    if (!this.selectOnFocus) return;

    // Defer to avoid interfering with native focus/click caret placement
    setTimeout(() => {
      const el = this.elementRef.nativeElement;
      if (document.activeElement === el) {
        el.select();
      }
    });
  }

  constructor(
    @Optional() @Self() @Inject(NgModel) protected override ngModel: NgModel,
    @Optional() @Self() @Inject(FormControlName) protected override formControl: FormControlName,
    protected override cdr: ChangeDetectorRef,
    protected override elementRef: ElementRef<HTMLInputElement>,
    private readonly _renderer: Renderer2
  ) {
    super(ngModel, formControl, cdr, elementRef, _renderer);
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // Ensure proper UX for decimal input
    this.type = 'text';
    this._renderer.setAttribute(this.elementRef.nativeElement, 'inputmode', 'decimal');

    const ctrl = this.formControl?.control ?? this.ngModel?.control;
    if (ctrl) {
      this.valueSub = ctrl.valueChanges.subscribe((v) => {
        if (this.normalizing) return;

        const el = this.elementRef.nativeElement;
        if (document.activeElement === el) return;

        // Incoming value may be a number — convert to string for display
        const asString = v == null ? '' : String(v);
        const normalized = this.normalize(asString);
        const displayed = this.decimals == null ? normalized : this.formatFixedDecimals(normalized);
        const target = this.toNumber(displayed);

        if (target !== v) {
          this.normalizing = true;
          ctrl.setValue(target);
          this.setNativeValue(displayed);
          this.normalizing = false;
        } else if (displayed !== asString) {
          // Display string needs updating even if control value is already correct
          this.setNativeValue(displayed);
        }
      });
    }

    // In case an initial value is already present on init
    const initial = this.elementRef.nativeElement.value;
    const normalizedInitial = this.normalize(initial);
    const formattedInitial = this.decimals == null ? normalizedInitial : this.formatFixedDecimals(normalizedInitial);
    const targetInitial = this.toNumber(formattedInitial);

    if (formattedInitial !== initial) {
      this.setNativeValue(formattedInitial);
    }
    const ctrl2 = this.formControl?.control ?? this.ngModel?.control;
    if (ctrl2 && targetInitial !== ctrl2.value) {
      this.normalizing = true;
      ctrl2.setValue(targetInitial);
      this.normalizing = false;
    }
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.valueSub?.unsubscribe();
  }

  // Normalize while user types
  @HostListener('input')
  public onInput(): void {
    const el = this.elementRef.nativeElement;
    const before = el.value;
    const after = this.normalize(before);

    if (after !== before) {
      const cursorOffset = before.length - (el.selectionStart ?? before.length);
      this.setNativeValue(after);

      // Try to keep caret near the end the user was editing
      const newPos = Math.max(0, after.length - cursorOffset);
      this.setSelection(newPos, newPos);
    }
  }

  @HostListener('blur')
  public override onBlur(): void {
    super.onBlur();

    const el = this.elementRef.nativeElement;
    const before = el.value;
    const normalized = this.normalize(before);

    // Format display value with fixed decimals (when decimals is set)
    const displayed = this.decimals == null ? normalized : this.formatFixedDecimals(normalized);
    const target = this.toNumber(displayed);

    const ctrl = this.formControl?.control ?? this.ngModel?.control;

    if (displayed !== before) {
      this.setNativeValue(displayed);
    }

    if (ctrl && target !== ctrl.value) {
      ctrl.setValue(target);
    }
  }

  private setNativeValue(v: string): void {
    this.elementRef.nativeElement.value = v;
  }

  private setSelection(start: number, end: number): void {
    try {
      this.elementRef.nativeElement.setSelectionRange(start, end);
    } catch {
      // some environments may not support setSelectionRange; safe to ignore
    }
  }

  private toNumber(s: string): number | null {
    if (!s || s === '-') return null;
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  }

  private normalize(val: unknown): string {
    if (val == null) return '';
    let s = String(val);

    // Replace commas with dots
    s = s.replaceAll(',', '.');

    // Remove all characters except digits, dot and minus
    s = s.replaceAll(/[^0-9.-]/g, '');

    // Allow a single leading minus only
    s = s.replaceAll(/(?!^)-/g, '');

    // Keep only the first dot
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      const intPart = s.slice(0, firstDot);
      let fracPart = s.slice(firstDot + 1).replaceAll('.', '');

      // If decimals is set, limit fractional digits while typing
      if (this.decimals != null && this.decimals >= 0) {
        fracPart = fracPart.slice(0, this.decimals);
      }

      s = `${intPart}.${fracPart}`;
    }

    return s;
  }

  private formatFixedDecimals(s: string): string {
    const d = this.decimals;
    if (d == null) return s;

    const decimals = Math.max(0, Math.floor(d));
    if (!s) return '';

    // Preserve lone "-" while user is entering a negative number
    if (s === '-') return s;

    const negative = s.startsWith('-');
    const raw = negative ? s.slice(1) : s;

    const [intRaw, fracRaw = ''] = raw.split('.');
    const intPart = intRaw === '' ? '0' : intRaw;
    const fracPart = fracRaw.padEnd(decimals, '0').slice(0, decimals);

    if (decimals === 0) {
      return `${negative ? '-' : ''}${intPart}`;
    }

    return `${negative ? '-' : ''}${intPart}.${fracPart}`;
  }
}
